import React, { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { useIntl } from '../../util/reactIntl';
import { useSdk } from '../../sdkContext';
import { fetchProductDetails } from './api';

import {
  Page,
  UserNav,
  LayoutSideNavigation,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperBusinessSideNav,
} from '../../components';
import { TopbarContainer } from '..';
import css from './ManageStockSeasonalityPage.css';

const MONTH_KEYS = [
  'ManageStockSeasonalityPage.monthJanuary',
  'ManageStockSeasonalityPage.monthFebruary',
  'ManageStockSeasonalityPage.monthMarch',
  'ManageStockSeasonalityPage.monthApril',
  'ManageStockSeasonalityPage.monthMay',
  'ManageStockSeasonalityPage.monthJune',
  'ManageStockSeasonalityPage.monthJuly',
  'ManageStockSeasonalityPage.monthAugust',
  'ManageStockSeasonalityPage.monthSeptember',
  'ManageStockSeasonalityPage.monthOctober',
  'ManageStockSeasonalityPage.monthNovember',
  'ManageStockSeasonalityPage.monthDecember',
];

const WEEKDAY_KEYS = [
  'ManageStockSeasonalityPage.weekdayMon',
  'ManageStockSeasonalityPage.weekdayTue',
  'ManageStockSeasonalityPage.weekdayWed',
  'ManageStockSeasonalityPage.weekdayThu',
  'ManageStockSeasonalityPage.weekdayFri',
  'ManageStockSeasonalityPage.weekdaySat',
  'ManageStockSeasonalityPage.weekdaySun',
];

const toYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const ManageStockSeasonalityPage = () => {
  const { listingId } = useParams();
  const history = useHistory();
  const sdk = useSdk();
  const intl = useIntl();
  const queryClient = useQueryClient();

  const user = useSelector(state => state.user);
  const currentUser = user?.currentUser;
  const currentUserProviders = user?.userProviders || [];

  const [plannedClosing, setPlannedClosing] = useState('');
  const [plannedPublishing, setPlannedPublishing] = useState('');
  const [startMonthOffset, setStartMonthOffset] = useState(0);
  const [saveError, setSaveError] = useState(null);

  const {
    data: productResponse,
    isLoading: isProductLoading,
    isError: isProductError,
  } = useQuery({
    queryKey: ['productDetails', listingId],
    queryFn: () => fetchProductDetails(sdk, listingId),
    enabled: Boolean(listingId),
    staleTime: 1000 * 60 * 5,
  });

  const product = useMemo(() => {
    return productResponse?.data?.data || productResponse?.data || null;
  }, [productResponse]);

  const includedEntities = useMemo(() => {
    return productResponse?.data?.included || productResponse?.included || [];
  }, [productResponse]);

  const providerIdFromProduct = useMemo(() => {
    return product?.relationships?.provider?.data?.id?.uuid 
      || product?.relationships?.provider?.data?.id 
      || null;
  }, [product]);

  const currentProvider = useMemo(() => {
    if (!providerIdFromProduct) return null;

    const includedProvider = includedEntities.find(
      item => (item.id?.uuid || item.id) === providerIdFromProduct
    );

    if (includedProvider) return includedProvider;

    return currentUserProviders.find(p => (p.id?.uuid || p.id) === providerIdFromProduct) || null;
  }, [providerIdFromProduct, includedEntities, currentUserProviders]);

  const userPermissions = useMemo(() => {
    if (!currentUser) {
      return {
        isAdmin: false,
        isOwner: false,
        isEmployee: false,
        roleLabel: intl.formatMessage({ id: 'ManageStockSeasonalityPage.roleNone' }),
      };
    }

    const { isAdmin = false, adminForCountries = [] } = currentUser.attributes || {};
    const providerCountry =
      currentProvider?.attributes?.country ||
      currentProvider?.attributes?.address?.country;

    const isCountryAdmin =
      Array.isArray(adminForCountries) &&
      providerCountry &&
      adminForCountries.includes(providerCountry);

    const isEffectiveAdmin = Boolean(isAdmin || isCountryAdmin);

    if (isEffectiveAdmin) {
      return {
        isAdmin: true,
        isOwner: true,
        isEmployee: true,
        roleLabel: isAdmin
          ? intl.formatMessage({ id: 'ManageStockSeasonalityPage.roleAdmin' })
          : intl.formatMessage(
              { id: 'ManageStockSeasonalityPage.roleCountryAdmin' },
              { country: providerCountry?.toUpperCase() }
            ),
      };
    }

    const myRole = currentProvider?.attributes?.myRole;
    const isOwner = myRole === 'owner';
    const isEmployee = myRole === 'employee' || isOwner;

    return {
      isAdmin: false,
      isOwner,
      isEmployee,
      roleLabel: isOwner
        ? intl.formatMessage({ id: 'ManageStockSeasonalityPage.roleOwner' })
        : isEmployee
        ? intl.formatMessage({ id: 'ManageStockSeasonalityPage.roleEmployee' })
        : intl.formatMessage({ id: 'ManageStockSeasonalityPage.roleNone' }),
    };
  }, [currentUser, currentProvider, intl]);

  const productTitle =
    product?.attributes?.title ||
    intl.formatMessage({ id: 'ManageStockSeasonalityPage.defaultProductTitle' });
  const providerName = currentProvider?.attributes?.name || currentProvider?.attributes?.title || '';
  const currentState = product?.attributes?.openedToday;
  const isCurrentlyOpen = !!currentState;

  const savedManagedData = useMemo(() => {
    return product?.attributes?.managedPiecesData || {};
  }, [product]);

  const savedClosing = savedManagedData.plannedClosing || '';
  const savedPublishing = savedManagedData.plannedPublishing || '';

  const isDirty = useMemo(() => {
    return plannedClosing !== savedClosing || plannedPublishing !== savedPublishing;
  }, [plannedClosing, plannedPublishing, savedClosing, savedPublishing]);

  useEffect(() => {
    if (product) {
      setPlannedClosing(savedClosing);
      setPlannedPublishing(savedPublishing);
    }
  }, [product, savedClosing, savedPublishing]);

  const { mutate: handleSaveDates, isLoading: isSaving } = useMutation({
    mutationFn: async ({ closingDate, publishingDate }) => {
      setSaveError(null);
      const listingsApi = sdk?.newSdk?.listings || sdk?.listings;

      if (!listingsApi) {
        throw new Error(intl.formatMessage({ id: 'ManageStockSeasonalityPage.errorSdkMissing' }));
      }

      return listingsApi.update({
        id: listingId,
        managedPiecesData: {
          plannedClosing: closingDate || null,
          plannedPublishing: publishingDate || null,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['productDetails', listingId]);
      queryClient.invalidateQueries(['ownListings']);
    },
    onError: () => {
      setSaveError(intl.formatMessage({ id: 'ManageStockSeasonalityPage.errorSaveFailed' }));
    },
  });

  const todayStr = useMemo(() => toYMD(new Date()), []);

  const monthNames = useMemo(() => {
    return MONTH_KEYS.map((key) => intl.formatMessage({ id: key }));
  }, [intl]);

  const weekdayNames = useMemo(() => {
    return WEEKDAY_KEYS.map((key) => intl.formatMessage({ id: key }));
  }, [intl]);

  const monthsData = useMemo(() => {
    const months = [];
    const now = new Date();
    const baseYear = now.getFullYear();
    const baseMonth = now.getMonth() + startMonthOffset;

    for (let i = 0; i < 12; i++) {
      const firstDay = new Date(baseYear, baseMonth + i, 1);
      const year = firstDay.getFullYear();
      const month = firstDay.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      let firstDayOfWeek = firstDay.getDay() - 1;
      if (firstDayOfWeek === -1) firstDayOfWeek = 6;

      const days = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const dateObj = new Date(year, month, d);
        const ymd = toYMD(dateObj);

        let isOpen = isCurrentlyOpen;

        if (isCurrentlyOpen) {
          if (savedClosing && ymd >= savedClosing) {
            isOpen = false;
          }
          if (savedPublishing && ymd >= savedPublishing) {
            isOpen = true;
          }
        } else {
          if (savedPublishing && ymd >= savedPublishing) {
            isOpen = true;
          }
          if (savedClosing && ymd >= savedClosing) {
            isOpen = false;
          }
        }

        days.push({
          dayNumber: d,
          ymd,
          isOpen,
          isToday: ymd === todayStr,
        });
      }

      months.push({
        year,
        monthName: monthNames[month],
        firstDayOfWeek,
        days,
      });
    }

    return months;
  }, [startMonthOffset, isCurrentlyOpen, savedClosing, savedPublishing, todayStr, monthNames]);

  const rangeTitle = useMemo(() => {
    if (monthsData.length === 0) return '';
    const first = monthsData[0];
    const last = monthsData[monthsData.length - 1];
    return `${first.monthName} ${first.year} – ${last.monthName} ${last.year}`;
  }, [monthsData]);

  const handleBackClick = () => {
    if (providerIdFromProduct) {
      history.push(`/stock/${providerIdFromProduct}`);
    } else {
      history.goBack();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaveError(null);

    if (isCurrentlyOpen) {
      if (plannedPublishing && !plannedClosing) {
        setSaveError(intl.formatMessage({ id: 'ManageStockSeasonalityPage.errorMissingClosing' }));
        return;
      }
      if (plannedClosing && plannedPublishing && plannedPublishing <= plannedClosing) {
        setSaveError(intl.formatMessage({ id: 'ManageStockSeasonalityPage.errorPublishingBeforeClosing' }));
        return;
      }
    } else {
      if (plannedClosing && !plannedPublishing) {
        setSaveError(intl.formatMessage({ id: 'ManageStockSeasonalityPage.errorMissingPublishing' }));
        return;
      }
      if (plannedPublishing && plannedClosing && plannedClosing <= plannedPublishing) {
        setSaveError(intl.formatMessage({ id: 'ManageStockSeasonalityPage.errorClosingBeforePublishing' }));
        return;
      }
    }

    handleSaveDates({
      closingDate: plannedClosing,
      publishingDate: plannedPublishing,
    });
  };

  return (
    <Page
      title={intl.formatMessage(
        { id: 'ManageStockSeasonalityPage.pageTitle' },
        { productTitle }
      )}
    >
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="ManageListingsPage" />
          <UserNav selectedPageName="ManageListingsPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperBusinessSideNav currentTab="ManageListingsPage" />
        <LayoutWrapperMain>
          <div className={css.roleBadgeContainer}>
            <span>
              {intl.formatMessage({ id: 'ManageStockSeasonalityPage.roleLabel' })}
            </span>
            <strong className={css.roleBadgeText}>{userPermissions.roleLabel}</strong>
          </div>

          <div className={css.topHeaderRow}>
            <button type="button" className={css.backBtn} onClick={handleBackClick}>
              {intl.formatMessage({ id: 'ManageStockSeasonalityPage.backToStock' })}
            </button>
          </div>

          {isProductLoading ? (
            <div className={css.loadingInsideLayout}>
              <div className={css.spinner} />
              <span>
                {intl.formatMessage({ id: 'ManageStockSeasonalityPage.loadingProduct' })}
              </span>
            </div>
          ) : isProductError ? (
            <div className={css.errorInsideLayout}>
              <span>
                {intl.formatMessage({ id: 'ManageStockSeasonalityPage.errorFetchingProduct' })}
              </span>
            </div>
          ) : (
            <>
              <div className={css.productCard}>
                <h1 className={css.productTitle}>{productTitle}</h1>
                {providerName && <div className={css.providerName}>{providerName}</div>}
              </div>

              <div className={css.availabilityGrid}>
                <div className={css.leftSidebar}>
                  <div className={css.cardPanel}>
                    <div className={css.compactStatusRow}>
                      <span className={css.compactStatusLabel}>
                        {intl.formatMessage({ id: 'ManageStockSeasonalityPage.currentStatus' })}
                      </span>
                      <span className={isCurrentlyOpen ? css.compactBadgeOpen : css.compactBadgeClosed}>
                        {isCurrentlyOpen
                          ? intl.formatMessage({ id: 'ManageStockSeasonalityPage.statusOpen' })
                          : intl.formatMessage({ id: 'ManageStockSeasonalityPage.statusClosed' })}
                      </span>
                    </div>
                  </div>

                  <div className={css.cardPanel}>
                    <div className={css.cardSectionHeader}>
                      {intl.formatMessage({ id: 'ManageStockSeasonalityPage.plannedChangesHeader' })}
                    </div>
                    <form onSubmit={handleSubmit} className={css.datesForm}>
                      {isCurrentlyOpen ? (
                        <>
                          <div className={css.inputGroup}>
                            <label htmlFor="plannedClosing" className={css.fieldLabel}>
                              {intl.formatMessage({ id: 'ManageStockSeasonalityPage.plannedClosingLabel' })}
                            </label>
                            <input
                              id="plannedClosing"
                              type="date"
                              value={plannedClosing}
                              onChange={(e) => setPlannedClosing(e.target.value)}
                              className={css.dateInput}
                            />
                          </div>

                          <div className={css.inputGroup}>
                            <label htmlFor="plannedPublishing" className={css.fieldLabel}>
                              {intl.formatMessage({ id: 'ManageStockSeasonalityPage.plannedPublishingLabel' })}
                            </label>
                            <input
                              id="plannedPublishing"
                              type="date"
                              value={plannedPublishing}
                              onChange={(e) => setPlannedPublishing(e.target.value)}
                              className={css.dateInput}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className={css.inputGroup}>
                            <label htmlFor="plannedPublishing" className={css.fieldLabel}>
                              {intl.formatMessage({ id: 'ManageStockSeasonalityPage.plannedPublishingLabel' })}
                            </label>
                            <input
                              id="plannedPublishing"
                              type="date"
                              value={plannedPublishing}
                              onChange={(e) => setPlannedPublishing(e.target.value)}
                              className={css.dateInput}
                            />
                          </div>

                          <div className={css.inputGroup}>
                            <label htmlFor="plannedClosing" className={css.fieldLabel}>
                              {intl.formatMessage({ id: 'ManageStockSeasonalityPage.plannedClosingLabel' })}
                            </label>
                            <input
                              id="plannedClosing"
                              type="date"
                              value={plannedClosing}
                              onChange={(e) => setPlannedClosing(e.target.value)}
                              className={css.dateInput}
                            />
                          </div>
                        </>
                      )}

                      {saveError && <div className={css.inlineError}>{saveError}</div>}

                      <button type="submit" disabled={!isDirty || isSaving} className={css.saveBtn}>
                        {isSaving
                          ? intl.formatMessage({ id: 'ManageStockSeasonalityPage.savingBtn' })
                          : intl.formatMessage({ id: 'ManageStockSeasonalityPage.saveBtn' })}
                      </button>
                    </form>
                  </div>

                  <div className={css.legendRow}>
                    <div className={css.legendItem}>
                      <span className={`${css.legendDot} ${css.dotOpen}`} />
                      <span>
                        {intl.formatMessage({ id: 'ManageStockSeasonalityPage.statusOpen' })}
                      </span>
                    </div>
                    <div className={css.legendItem}>
                      <span className={`${css.legendDot} ${css.dotClosed}`} />
                      <span>
                        {intl.formatMessage({ id: 'ManageStockSeasonalityPage.statusClosed' })}
                      </span>
                    </div>
                    <div className={css.legendItem}>
                      <span className={`${css.legendDot} ${css.dotToday}`} />
                      <span>
                        {intl.formatMessage({ id: 'ManageStockSeasonalityPage.legendToday' })}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={css.calendarContainer}>
                  <div className={css.calendarHeader}>
                    <button
                      type="button"
                      className={css.navArrowBtn}
                      onClick={() => setStartMonthOffset((prev) => prev - 12)}
                    >
                      ‹
                    </button>
                    <span className={css.calendarRangeTitle}>{rangeTitle}</span>
                    <button
                      type="button"
                      className={css.navArrowBtn}
                      onClick={() => setStartMonthOffset((prev) => prev + 12)}
                    >
                      ›
                    </button>
                  </div>

                  <div className={css.monthsGrid}>
                    {monthsData.map((month) => (
                      <div key={`${month.year}-${month.monthName}`} className={css.monthCard}>
                        <div className={css.monthTitle}>
                          {month.monthName} {month.year}
                        </div>

                        <div className={css.weekdaysHeader}>
                          {weekdayNames.map((w, idx) => (
                            <span key={`${w}-${idx}`}>{w}</span>
                          ))}
                        </div>

                        <div className={css.daysGrid}>
                          {Array.from({ length: month.firstDayOfWeek }).map((_, idx) => (
                            <div key={`empty-${idx}`} className={css.emptyCell} />
                          ))}

                          {month.days.map((day) => {
                            const cellClass = `${css.dayCell} ${
                              day.isOpen ? css.dayOpen : css.dayClosed
                            } ${day.isToday ? css.dayToday : ''}`;

                            return (
                              <div key={day.ymd} className={cellClass}>
                                {day.dayNumber}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </LayoutWrapperMain>
      </LayoutSideNavigation>
    </Page>
  );
};

export default ManageStockSeasonalityPage;